import { parseISO, isValid, format, parse } from 'date-fns';

// =============================================================================
// Date helpers
//
// The app stores structured deadlines (`deadline`, `effective_deadline`) as
// ISO date strings ("2026-04-30") or null. But inbox items carry a loose,
// human-readable `deadline_approx` field ("April 30, 2026", "Due May 31",
// etc.) populated by the extension's per-site parsers.
//
// These helpers bridge the two:
//   - safeParseISO / safeFormatDate: defensive rendering. Never throw, even
//     if a malformed string slipped into a date field. A bad value renders
//     as null rather than crashing the page (date-fns `format()` throws
//     RangeError on an invalid Date, which unmounts the whole React tree).
//   - normalizeToIso: best-effort free-text -> ISO conversion, used at
//     promotion time when a loose inbox `deadline_approx` becomes a
//     structured scholarship `deadline`.
// =============================================================================

/**
 * Parse a string into a Date, returning null instead of an Invalid Date when
 * the input isn't a valid ISO date string. Safe to call on any value.
 */
export function safeParseISO(value: string | null | undefined): Date | null {
  if (!value || typeof value !== 'string') return null;
  const d = parseISO(value);
  return isValid(d) ? d : null;
}

/**
 * Format a date-string field for display. Returns null when the value is
 * missing or unparseable, so callers can render a fallback ("TBD") rather
 * than crashing. `fmt` is a date-fns format pattern.
 */
export function safeFormatDate(
  value: string | null | undefined,
  fmt: string
): string | null {
  const d = safeParseISO(value);
  if (!d) return null;
  try {
    return format(d, fmt);
  } catch {
    // Defensive: format() should not throw once isValid() passed, but never
    // let a date format crash a render.
    return null;
  }
}

/**
 * Best-effort conversion of a free-text date string into an ISO date string
 * ("2026-04-30"). Returns null when the input can't be confidently parsed.
 *
 * Used at inbox-promotion time: the extension's parsers emit human-readable
 * deadline text ("April 30, 2026"), which must become an ISO string when the
 * inbox item is promoted to a structured scholarship. If conversion fails,
 * the caller keeps the original text in `deadline_notes` so no info is lost.
 *
 * Handles these input shapes:
 *   - Already-ISO: "2026-04-30"            -> "2026-04-30"
 *   - ISO datetime: "2026-04-30T23:59:59Z" -> "2026-04-30"
 *   - Long month:  "April 30, 2026"        -> "2026-04-30"
 *   - Short month: "Apr 30, 2026"          -> "2026-04-30"
 *   - US numeric:  "4/30/2026", "04/30/26" -> "2026-04-30"
 *   - "Due:" prefix is stripped before parsing.
 */
export function normalizeToIso(value: string | null | undefined): string | null {
  if (!value || typeof value !== 'string') return null;

  // Strip common prefixes the parsers or sites may include.
  let text = value.trim().replace(/^due:?\s*/i, '').trim();
  if (!text) return null;

  // 1. Already an ISO date or datetime — parseISO handles both.
  const iso = parseISO(text);
  if (isValid(iso)) return format(iso, 'yyyy-MM-dd');

  // 2. Try a series of explicit format patterns. date-fns `parse` is strict:
  //    it only succeeds on an exact pattern match, so we try each in turn.
  //    Reference date is "now" but only matters for fields the pattern omits
  //    (none of these omit a year, so it's not actually consulted).
  const patterns = [
    'MMMM d, yyyy',   // April 30, 2026
    'MMM d, yyyy',    // Apr 30, 2026
    'MMMM d yyyy',    // April 30 2026 (no comma)
    'MMM d yyyy',     // Apr 30 2026
    'M/d/yyyy',       // 4/30/2026
    'MM/dd/yyyy',     // 04/30/2026
    'M/d/yy',         // 4/30/26
    'MM/dd/yy',       // 04/30/26
    'yyyy-MM-dd',     // (redundant with parseISO, kept for completeness)
  ];
  for (const pattern of patterns) {
    const d = parse(text, pattern, new Date());
    if (isValid(d)) {
      // Sanity range-check: reject years far outside a plausible scholarship
      // window so a misparse doesn't yield e.g. year 0030.
      const year = d.getFullYear();
      if (year >= 2000 && year <= 2100) {
        return format(d, 'yyyy-MM-dd');
      }
    }
  }

  // 3. Couldn't confidently parse — caller should preserve the original text.
  return null;
}
